import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import ReactPlayer from 'react-player';
import { supabase } from '@/integrations/supabase/client';
import type { CapabilityDetail, UseCaseDetail } from '@/types/solution';

export interface FeaturedMediaLink {
  url: string;
  solutionId: string;
  solutionName: string;
  solutionSlug: string;
  solutionImage?: string;
  shortDescription: string;
  category: string;
  context: string;
}

interface Options {
  count?: number;
  featuredOnly?: boolean;
}

const shuffle = <T,>(items: T[]): T[] => {
  const arr = [...items];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const collectMediaLinks = (rows: any[]): FeaturedMediaLink[] => {
  const out: FeaturedMediaLink[] = [];

  for (const row of rows) {
    const details = row.details as { capabilities?: any; useCases?: any } | null;
    if (!details) continue;

    const category = row.solution_categories?.[0]?.categories?.name || 'Other';

    const pushIfValid = (mediaLink: unknown, contextTitle: string) => {
      if (typeof mediaLink !== 'string' || !mediaLink) return;
      if (!ReactPlayer.canPlay(mediaLink)) return;
      out.push({
        url: mediaLink,
        solutionId: row.id,
        solutionName: row.name,
        solutionSlug: row.slug,
        solutionImage: row.image_url || undefined,
        shortDescription: row.short_description || '',
        category,
        context: contextTitle,
      });
    };

    if (Array.isArray(details.capabilities)) {
      for (const cap of details.capabilities as CapabilityDetail[]) {
        if (cap && typeof cap === 'object') {
          pushIfValid((cap as any).mediaLink, cap.title || '');
        }
      }
    }

    if (Array.isArray(details.useCases)) {
      for (const uc of details.useCases as UseCaseDetail[]) {
        if (uc && typeof uc === 'object') {
          pushIfValid((uc as any).mediaLink, uc.title || '');
        }
      }
    }
  }

  return out;
};

export const useFeaturedMediaLinks = ({ count = 3, featuredOnly = false }: Options = {}) => {
  const queryKey = ['featured-media-links', { count, featuredOnly }];

  const { data, isLoading, isError, error } = useQuery<FeaturedMediaLink[], Error>({
    queryKey,
    queryFn: async () => {
      let query = supabase
        .from('solutions')
        .select(`
          id,
          name,
          slug,
          image_url,
          short_description,
          details,
          featured,
          solution_categories(categories:category_id(name))
        `)
        .or('status.is.null,status.neq.classified');

      if (featuredOnly) {
        query = query.eq('featured', true);
      }

      const { data: rows, error: queryError } = await query;
      if (queryError) throw queryError;

      return collectMediaLinks((rows ?? []) as any[]);
    },
    refetchOnWindowFocus: false,
    staleTime: 30 * 60 * 1000,
    retry: 1,
  });

  const selected = useMemo(() => {
    const links = (data ?? []) as FeaturedMediaLink[];
    if (links.length === 0) return [];
    return shuffle(links).slice(0, count);
  }, [data, count]);

  return {
    mediaLinks: selected,
    isLoading,
    isError,
    error: error as Error | null,
  };
};
